'use client'

import Link from 'next/link'
import { ArrowRight, Lightbulb, Zap, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { analytics } from '@/lib/analytics'

export default function CreatorsPage() {
  return (
    <div className="bg-background text-charcoal">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Create more.
              <br />
              <span className="text-primary">Stress less.</span>
            </h1>
            <p className="text-lg sm:text-xl text-warm-gray mb-8 leading-relaxed">
              Batch your content, never miss a caption, generate thumbnails that actually pop. Stop being a creator stuck in admin mode.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'signup', persona: 'creator' })}>
                <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" className="border-warm-gray text-charcoal hover:bg-primary/5 px-8 py-6 text-lg" asChild>
                <a href="#for-you" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'learn_more', persona: 'creator' })}>See what's possible</a>
              </Button>
            </div>
            <p className="text-sm text-warm-gray mt-4">No credit card required &middot; 5 free sessions included</p>
          </div>
        </div>
      </section>

      {/* For You Section */}
      <section id="for-you" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Tools built for your creative flow</h2>
            <p className="text-warm-gray text-lg">Stop spending hours on the boring stuff.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Batch like a pro */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <BarChart3 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Batch like a pro</h3>
              <p className="text-warm-gray leading-relaxed">
                Record 10 videos once a month? Spend 1 hour getting all the captions, descriptions, and tags done at once. Then you're free.
              </p>
            </div>

            {/* Never miss an angle */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Lightbulb className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Ideas on demand</h3>
              <p className="text-warm-gray leading-relaxed">
                Stuck on a thumbnail? Need a video title? Get 5 options in 30 seconds. Pick one or remix them. Creativity meets speed.
              </p>
            </div>

            {/* Repurpose smarter */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">One piece, many places</h3>
              <p className="text-warm-gray leading-relaxed">
                Write one script. Get a short-form version for TikTok, a tweet thread, a LinkedIn post, and a newsletter angle. Done in minutes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How creators are using it</h2>
            <p className="text-warm-gray text-lg">Real examples. Real time savings.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Write better YouTube titles and descriptions',
                description: 'Paste your topic or transcript. Get 5 title options that hit keywords and actually get clicked. AI understands what works on YouTube.',
                example: 'You: "I reviewed 10 AI tools for podcasters"\nAI: 3 titles that drive clicks + SEO-optimized descriptions'
              },
              {
                title: 'Generate captions that match your tone',
                description: 'Feed AI your channel vibe. It learns how you write. Now every post sounds like you, stays consistent, saves hours.',
                example: 'You record a video. Paste the transcript. Get caption options in 5 minutes, not 30.'
              },
              {
                title: 'Create social media calendars in bulk',
                description: 'Plan a month of posts in one go. AI drafts captions, suggests posting times, generates hashtags. Batch creation at its best.',
                example: 'Old way: 2 hours planning + writing + posting\nAI way: 30 minutes planning, AI does the writing.'
              },
              {
                title: 'Turn one piece into a series',
                description: 'Write a long-form blog or email. AI extracts social clips, LinkedIn posts, email follow-ups, thread ideas. Multiply your reach.',
                example: '1 essay → 10 social posts + email series + short video scripts'
              }
            ].map((useCase, i) => (
              <div key={i} className="p-8 bg-white rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{useCase.title}</h3>
                <p className="text-warm-gray mb-6 leading-relaxed">{useCase.description}</p>
                <div className="bg-background p-4 rounded border border-primary/10">
                  <p className="text-sm font-mono text-charcoal whitespace-pre-wrap">{useCase.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Creators are already going faster</h2>
            <p className="text-warm-gray text-lg">See what people with audiences are doing</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "Used to spend 4 hours writing social captions for my week of videos. Now it's 30 minutes. And they get more engagement.",
                author: 'Alex Chen',
                role: 'YouTube creator, 150K subs',
                initials: 'AC',
                avatarClass: 'bg-red-100 text-red-700',
              },
              {
                quote: 'I batch my podcast episodes and use AI to generate show notes, email excerpts, and Twitter threads. Feels like I hired an editor.',
                author: 'Jordan Maxwell',
                role: 'Podcast host',
                initials: 'JM',
                avatarClass: 'bg-purple-100 text-purple-700',
              },
              {
                quote: 'Taught me how to use AI for video descriptions without sounding robotic. My click-through rate actually went up.',
                author: 'Taylor Brooks',
                role: 'TikTok creator, 250K followers',
                initials: 'TB',
                avatarClass: 'bg-cyan-100 text-cyan-700',
              },
            ].map((testimonial, i) => (
              <div key={i} className="p-8 bg-background rounded-lg border border-gray-200">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-warning">★</span>
                  ))}
                </div>
                <p className="text-charcoal mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.avatarClass} flex items-center justify-center text-sm font-semibold flex-shrink-0`}>
                    {testimonial.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">{testimonial.author}</p>
                    <p className="text-sm text-warm-gray">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Common creator questions</h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "Will my audience tell if I'm using AI?",
                a: "Only if you let them. AI is a tool—like using Adobe or Canva. You use it to make better work faster, then you add your voice. That part is always you."
              },
              {
                q: 'Will AI replace my creativity?',
                a: 'Nope. It handles the grunt work. You still decide what to create, what angle to take, what to say. AI just stops you from wasting 2 hours on a description.'
              },
              {
                q: 'Can I use it for sponsored content?',
                a: "Yes, but disclose that you're using AI tools—same as you'd disclose Photoshop or Final Cut. Transparency builds trust with your audience anyway."
              },
              {
                q: "What if I'm worried about copyright?",
                a: "AI creates new text based on prompts you give it. Just don't paste other creators' work and ask AI to rewrite it. Use it for your original ideas, and you're good."
              }
            ].map((faq, i) => (
              <div key={i} className="p-6 bg-white rounded-lg border border-gray-200">
                <h3 className="font-semibold text-charcoal mb-3 text-lg">{faq.q}</h3>
                <p className="text-warm-gray leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Create faster. Stress less.</h2>
          <p className="text-lg text-warm-gray mb-8">
            Try it free. See how much time you get back.
          </p>

          <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'footer_cta', cta: 'signup', persona: 'creator' })}>
            <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}
